import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdSave, 
  MdInventory,
  MdCategory,
  MdNumbers,
  MdLocationOn,
  MdClose,
  MdCheck,
  MdError,
  MdPerson,
  MdReceipt,
  MdTag,
  MdAccessTime,
  MdArrowForward,
  MdCalendarToday,
  MdSearch,
  MdDateRange,
  MdCheckBox,
  MdCheckBoxOutlineBlank
} from 'react-icons/md';
import toast from 'react-hot-toast';
import axios from 'axios';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';

const Export = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    purchaseOrder: '',
    lotEntry: '',
    exportDate: new Date().toISOString().split('T')[0] // Initialize with today's date
  });
  const [errors, setErrors] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [fifoProducts, setFifoProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLane, setSelectedLane] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const itemsPerPage = 10;

  // Helper function to calculate total quantity
  const calculateTotalQuantity = () => {
    return selectedProducts.reduce((sum, product) => {
      const quantity = product.exportQuantity;
      return sum + (quantity && quantity !== '' ? parseInt(quantity) : 0);
    }, 0);
  };

  // Fetch data from API
  useEffect(() => {
    const fetchFifoProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
        if (response.data && response.data.data) {
          // Flatten all batch details into a single array and filter out negative quantities
          const allBatches = response.data.data.flatMap(product => 
            product.batch_details
              .map(batch => ({
                ...batch,
                productType: product.product_type,
                total_quantity: product.total_quantity,
                quantity: parseInt(batch.batch_actual_stock) // Use batch_actual_stock for quantity
              }))
              .filter(batch => batch.quantity > 0) // Filter out batches with quantity <= 0
          );

          // Sort by date (FIFO)
          const sortedBatches = allBatches.sort((a, b) => new Date(a.date) - new Date(b.date));

          // Add priority based on age
          const batchesWithPriority = sortedBatches.map(batch => ({
            ...batch,
            priority: batch.age > 180 ? 'High' : batch.age > 90 ? 'Medium' : 'Low'
          }));

          setFifoProducts(batchesWithPriority);
        }
      } catch (err) {
        console.error('Error:', err);
        toast.error('Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFifoProducts();
  }, []);

  // Mock data for available products
  // const availableProducts = [
  //   { name: 'WW 450', quantity: 500, location: 'Lane 1, Slot 3' },
  //   { name: 'W2 450', quantity: 300, location: 'Lane 2 A, Slot 1' },
  //   { name: 'WW 210', quantity: 200, location: 'Lane 3 A, Slot 2' },
  //   { name: 'W2 210', quantity: 150, location: 'Lane 4 A, Slot 4' },
  //   { name: 'WW 240', quantity: 400, location: 'Lane 5 A, Slot 1' },
  //   { name: 'WW 320', quantity: 250, location: 'Lane 6 A, Slot 3' },
  //   { name: 'WW 240s', quantity: 180, location: 'Lane 7 A, Slot 2' },
  //   { name: 'WW 320s', quantity: 220, location: 'Lane 8 A, Slot 1' },
  //   { name: 'W2 240', quantity: 350, location: 'Lane 1, Slot 4' },
  //   { name: 'W2 320', quantity: 280, location: 'Lane 2 B, Slot 2' },
  //   { name: 'SK1', quantity: 150, location: 'Lane 3 B, Slot 3' },
  //   { name: 'W2 360', quantity: 200, location: 'Lane 4 B, Slot 1' },
  //   { name: 'W3', quantity: 300, location: 'Lane 5 B, Slot 2' },
  //   { name: 'SK2', quantity: 250, location: 'Lane 6 B, Slot 4' },
  //   { name: 'SK3', quantity: 180, location: 'Lane 7 B, Slot 3' },
  //   { name: 'SP1 BAG', quantity: 400, location: 'Lane 8 B, Slot 2' },
  //   { name: 'W3M', quantity: 220, location: 'Lane 1, Slot 1' },
  //   { name: 'B2', quantity: 150, location: 'Lane 2 A, Slot 3' },
  //   { name: 'S1', quantity: 280, location: 'Lane 3 A, Slot 4' },
  //   { name: 'S2', quantity: 320, location: 'Lane 4 A, Slot 2' },
  //   { name: 'S3', quantity: 190, location: 'Lane 5 A, Slot 3' },
  //   { name: 'P1', quantity: 260, location: 'Lane 6 A, Slot 1' },
  //   { name: 'P2', quantity: 310, location: 'Lane 7 A, Slot 4' },
  //   { name: 'P3', quantity: 240, location: 'Lane 8 A, Slot 3' },
  //   { name: 'SP1', quantity: 170, location: 'Lane 1, Slot 2' },
  //   { name: 'SP2', quantity: 290, location: 'Lane 2 B, Slot 4' },
  //   { name: 'SP3', quantity: 330, location: 'Lane 3 B, Slot 1' },
  //   { name: 'SSP1', quantity: 210, location: 'Lane 4 B, Slot 3' },
  //   { name: 'SSP2', quantity: 270, location: 'Lane 5 B, Slot 4' },
  //   { name: 'SSP3', quantity: 380, location: 'Lane 6 B, Slot 2' },
  //   { name: 'X3', quantity: 420, location: 'Lane 7 B, Slot 1' }
  // ];

  // Handle FIFO product selection (multiple)
  const handleFifoProductSelect = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      if (isSelected) {
        // Remove from selection
        return prev.filter(p => p.id !== product.id);
      } else {
        // Add to selection with default quantity
        return [...prev, { ...product, exportQuantity: product.quantity }];
      }
    });
    setErrors({});
  };

  // Handle inline quantity change in FIFO list
  const handleInlineQuantityChange = (productId, newQuantity) => {
    setSelectedProducts(prev => 
      prev.map(product => 
        product.id === productId 
          ? { ...product, exportQuantity: newQuantity === '' ? '' : parseInt(newQuantity) || 0 }
          : product
      )
    );
    setErrors({});
  };

  // Handle quantity change for a specific product
  const handleProductQuantityChange = (productId, newQuantity) => {
    setSelectedProducts(prev => 
      prev.map(product => 
        product.id === productId 
          ? { ...product, exportQuantity: newQuantity === '' ? '' : parseInt(newQuantity) || 0 }
          : product
      )
    );
    setErrors({});
  };

  const handleCustomerNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      customerName: value
    }));
    setErrors({});
  };

  const handlePurchaseOrderChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      purchaseOrder: value
    }));
    setErrors({});
  };

  const handleLotEntryChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      lotEntry: value
    }));
    setErrors({});
  };

  const handleExportDateChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      exportDate: value
    }));
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (selectedProducts.length === 0) {
      newErrors.products = 'Please select at least one product from the FIFO queue';
    }
    
    // Validate each selected product
    selectedProducts.forEach((product, index) => {
      const quantity = product.exportQuantity;
      if (!quantity || quantity === '' || quantity <= 0) {
        newErrors[`quantity_${index}`] = 'Please enter a valid quantity';
      } else if (quantity > product.quantity) {
        newErrors[`quantity_${index}`] = `Quantity cannot exceed available amount (${product.quantity} units)`;
      }
    });

    if (!formData.exportDate) {
      newErrors.exportDate = 'Please select an export date';
    }
    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Please enter customer name';
    }
    // Purchase Order and Lot Entry are now optional
    // if (!formData.purchaseOrder.trim()) {
    //   newErrors.purchaseOrder = 'Please enter purchase order number';
    // }
    // if (!formData.lotEntry.trim()) {
    //   newErrors.lotEntry = 'Please enter lot entry';
    // }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    try {
      // Prepare bulk export data
      const bulkExportData = selectedProducts.map(product => ({
        batch: product.batch_id,
        product_type: product.productType,
        storage_date: product.date,
        quantity: product.exportQuantity,
        lane: product.lane || product.zone,
        slot: product.slot || product.rack,
        customer_name: formData.customerName,
        export_date: formData.exportDate,
        purchase_order: formData.purchaseOrder || null, // Optional
        lot_entry: formData.lotEntry || null // Optional
      }));

      // Send bulk export request
      const response = await axios.post('https://usibras-web-backend.onrender.com/api/export', bulkExportData);
      
      if (response.data) {
        setToast({
          show: true,
          message: response.data.message || `Successfully exported ${selectedProducts.length} product(s)!`,
          type: 'success'
        });
        
        // Reset form and close preview
        setFormData({
          customerName: '',
          purchaseOrder: '',
          lotEntry: '',
          exportDate: new Date().toISOString().split('T')[0]
        });
        setSelectedProducts([]);
        setShowPreview(false);

        // Reload FIFO products data
        const fetchFifoProducts = async () => {
          try {
            const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
            if (response.data && response.data.data) {
              // Flatten all batch details into a single array and filter out negative quantities
              const allBatches = response.data.data.flatMap(product => 
                product.batch_details
                  .map(batch => ({
                    ...batch,
                    productType: product.product_type,
                    total_quantity: product.total_quantity,
                    quantity: parseInt(batch.batch_actual_stock)
                  }))
                  .filter(batch => batch.quantity > 0)
              );

              // Sort by date (FIFO)
              const sortedBatches = allBatches.sort((a, b) => new Date(a.date) - new Date(b.date));

              // Add priority based on age
              const batchesWithPriority = sortedBatches.map(batch => ({
                ...batch,
                priority: batch.age > 180 ? 'High' : batch.age > 90 ? 'Medium' : 'Low'
              }));

              setFifoProducts(batchesWithPriority);
            }
          } catch (err) {
            console.error('Error:', err);
            toast.error('Failed to refresh products data');
          }
        };
        
        await fetchFifoProducts();
      }
    } catch (error) {
      console.error('Error:', error);
      setToast({
        show: true,
        message: error.response?.data?.message || 'Failed to export products. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get unique lanes for filter dropdown
  const uniqueLanes = [...new Set(fifoProducts.map(product => 
    product.lane || `Lane ${product.zone}`
  ))].sort();

  // Filter products based on search term and lane filter
  const filteredProducts = fifoProducts.filter(product => {
    const searchTermLower = searchTerm.toLowerCase();
    const productLane = product.lane || `Lane ${product.zone}`;
    
    // Apply lane filter
    if (selectedLane && productLane !== selectedLane) {
      return false;
    }
    
    // Apply search term filter
    return (
      product.productType.toLowerCase().includes(searchTermLower) ||
      product.quantity.toString().includes(searchTermLower) ||
      `${productLane}, Slot ${product.slot || product.rack}`.toLowerCase().includes(searchTermLower) ||
      product.batch_id.toLowerCase().includes(searchTermLower)
    );
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="mt-5 p-4">
      {/* Toast Notification */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          position="top-right"
          duration={3000}
        />
      )}

      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transform hover:scale-105 transition-all duration-200"
          >
            <div className="bg-blue-50 p-2 rounded-lg transform rotate-3 shadow-md">
              <MdArrowBack size={24} />
            </div>
            <span className="font-medium">Back to Products</span>
          </button>
        </div>
      </div>

      {/* Page Header */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-4 rounded-lg transform rotate-3 shadow-lg">
              <MdInventory className="text-white" size={32} />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Export Finished Product</h1>
          </div>
        </div>
      </div>

      {/* Customer Information Form */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-blue-50 p-2 rounded-lg">
              <MdPerson className="text-blue-600" size={24} />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Customer Information</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Customer Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center space-x-2">
                  <MdPerson className="text-blue-500" size={20} />
                  <span>Customer Name</span>
                </div>
              </label>
              <input
                type="text"
                value={formData.customerName}
                onChange={handleCustomerNameChange}
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  errors.customerName ? 'border-red-300' : 'border-gray-300'
                } focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Enter customer name"
              />
              {errors.customerName && (
                <p className="mt-1 text-sm text-red-600">{errors.customerName}</p>
              )}
            </div>

            {/* Export Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center space-x-2">
                  <MdDateRange className="text-blue-500" size={20} />
                  <span>Export Date</span>
                </div>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdCalendarToday className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  value={formData.exportDate}
                  onChange={handleExportDateChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`mt-1 block w-full pl-10 rounded-md shadow-sm ${
                    errors.exportDate ? 'border-red-300' : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500`}
                />
              </div>
              {errors.exportDate && (
                <p className="mt-1 text-sm text-red-600">{errors.exportDate}</p>
              )}
            </div>

            {/* Purchase Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center space-x-2">
                  <MdReceipt className="text-blue-500" size={20} />
                  <span>Purchase Order <span className="text-gray-500 text-xs">(Optional)</span></span>
                </div>
              </label>
              <input
                type="text"
                value={formData.purchaseOrder}
                onChange={handlePurchaseOrderChange}
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  errors.purchaseOrder ? 'border-red-300' : 'border-gray-300'
                } focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Enter PO number (optional)"
              />
              {errors.purchaseOrder && (
                <p className="mt-1 text-sm text-red-600">{errors.purchaseOrder}</p>
              )}
            </div>

            {/* Lot Entry */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center space-x-2">
                  <MdTag className="text-blue-500" size={20} />
                  <span>Lot Entry <span className="text-gray-500 text-xs">(Optional)</span></span>
                </div>
              </label>
              <input
                type="text"
                value={formData.lotEntry}
                onChange={handleLotEntryChange}
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  errors.lotEntry ? 'border-red-300' : 'border-gray-300'
                } focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Enter lot entry (optional)"
              />
              {errors.lotEntry && (
                <p className="mt-1 text-sm text-red-600">{errors.lotEntry}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FIFO Product List */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-50 p-2 rounded-lg">
                <MdAccessTime className="text-blue-600" size={24} />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">FIFO Product Queue</h2>
            </div>
            <div className="text-sm text-gray-500">
              {loading ? 'Loading products...' : `Showing ${filteredProducts.length} products sorted by date`}
            </div>
          </div>

          {/* Static Total Display */}
          {selectedProducts.length > 0 && (
            <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <MdInventory className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      Selected for Export
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">
                    {calculateTotalQuantity()} boxes
                  </div>
                  <div className="text-sm text-gray-500">
                    Total Quantity
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar and Lane Filter */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search Input */}
              <div className="md:col-span-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MdSearch className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Search by product type, quantity, or location..."
                    disabled={loading}
                  />
                </div>
              </div>
              
              {/* Lane Filter */}
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MdLocationOn className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    value={selectedLane}
                    onChange={(e) => {
                      setSelectedLane(e.target.value);
                      setCurrentPage(1); // Reset to first page on filter change
                    }}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    disabled={loading}
                  >
                    <option value="">All Lanes</option>
                    {uniqueLanes.map((lane) => (
                      <option key={lane} value={lane}>
                        {lane}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Active Filters Display */}
            {(searchTerm || selectedLane) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {searchTerm && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Search: "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm('')}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500"
                    >
                      <MdClose size={12} />
                    </button>
                  </span>
                )}
                {selectedLane && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Lane: {selectedLane}
                    <button
                      onClick={() => setSelectedLane('')}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500"
                    >
                      <MdClose size={12} />
                    </button>
                  </span>
                )}
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedLane('');
                  }}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>

          {/* Products List with Scrollbar */}
          <div className="max-h-[600px] overflow-y-auto pr-2">
            {loading ? (
              <div className="flex h-[400px] items-center justify-center">
                <div className="relative">
                  <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-blue-500"></div>
                  <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
                  <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500"></div>
                  <div className="mt-8 text-center">
                    <p className="text-lg font-medium text-gray-600">Loading</p>
                    <p className="text-sm text-gray-400">Please wait...</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {paginatedProducts.map((product, index) => {
                  const isSelected = selectedProducts.some(p => p.id === product.id);
                  return (
                    <div
                      key={product.id}
                      onClick={() => handleFifoProductSelect(product)}
                      className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : index === 0 && currentPage === 1
                          ? 'border-blue-200 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      } transition-all duration-200`}
                    >
                      <div className="flex items-center space-x-4">
                        {/* Checkbox */}
                        <div className="flex items-center justify-center w-6 h-6">
                          {isSelected ? (
                            <MdCheckBox className="text-blue-600" size={24} />
                          ) : (
                            <MdCheckBoxOutlineBlank className="text-gray-400" size={24} />
                          )}
                        </div>
                        
                        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                          index === 0 && currentPage === 1 ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <span className={`text-lg font-semibold ${
                            index === 0 && currentPage === 1 ? 'text-blue-600' : 'text-gray-600'
                          }`}>
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{product.productType}</h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm font-medium text-blue-600">
                              {product.batch_id}
                            </span>
                            <span className="text-sm text-gray-500">
                              <MdCalendarToday className="inline-block mr-1" size={16} />
                              {new Date(product.date).toLocaleDateString()}
                            </span>
                            <span className="text-sm text-gray-500">
                              <MdInventory className="inline-block mr-1" size={16} />
                              {product.quantity} boxes
                            </span>
                            <span className="text-sm text-gray-500">
                              <MdLocationOn className="inline-block mr-1" size={16} />
                              {product.lane || `Lane ${product.zone}`}, Slot {product.slot || product.rack}
                            </span>
                          </div>
                        </div>
                        
                        {/* Inline Quantity Editor */}
                        {isSelected && (
                          <div className="flex items-center space-x-3 ml-4">
                            <div className="flex items-center space-x-2">
                              <label className="text-sm font-medium text-gray-700">Export:</label>
                              <input
                                type="number"
                                value={selectedProducts.find(p => p.id === product.id)?.exportQuantity || ''}
                                onChange={(e) => handleInlineQuantityChange(product.id, e.target.value)}
                                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                min="1"
                                max={product.quantity}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-sm text-gray-500">/ {product.quantity}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          product.priority === 'High'
                            ? 'bg-red-100 text-red-700'
                            : product.priority === 'Medium'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {product.priority} Priority
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700`}>
                          {product.status}
                        </span>
                        {index === 0 && currentPage === 1 && (
                          <span className="flex items-center text-blue-600 font-medium">
                            <MdArrowForward className="mr-1" size={20} />
                            Next to Export
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Page Info */}
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages} • {filteredProducts.length} total items
              </div>
              
              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ←
                </button>
                
                {/* Page Numbers - Show limited pages */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  
                  if (totalPages <= maxVisiblePages) {
                    // Show all pages if total is small
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Show smart pagination
                    if (currentPage <= 3) {
                      // Near start
                      for (let i = 1; i <= 4; i++) {
                        pages.push(i);
                      }
                      pages.push('...');
                      pages.push(totalPages);
                    } else if (currentPage >= totalPages - 2) {
                      // Near end
                      pages.push(1);
                      pages.push('...');
                      for (let i = totalPages - 3; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Middle
                      pages.push(1);
                      pages.push('...');
                      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                        pages.push(i);
                      }
                      pages.push('...');
                      pages.push(totalPages);
                    }
                  }
                  
                  return pages.map((page, index) => (
                    <button
                      key={index}
                      onClick={() => typeof page === 'number' && setCurrentPage(page)}
                      disabled={page === '...'}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        page === currentPage
                          ? 'bg-blue-600 text-white'
                          : page === '...'
                          ? 'text-gray-400 cursor-default'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ));
                })()}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Export Form */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Selected Products Summary */}
          {selectedProducts.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Selected Products Summary</h3>
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 px-4 py-2 rounded-lg">
                    <span className="text-sm font-medium text-blue-700">
                      Total Quantity: {calculateTotalQuantity()} boxes
                    </span>
                  </div>
                  <div className="bg-green-100 px-4 py-2 rounded-lg">
                    <span className="text-sm font-medium text-green-700">
                      {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {selectedProducts.map((product, index) => (
                  <div key={product.id} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <MdCategory className="text-blue-600" size={16} />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{product.productType}</h4>
                          <p className="text-sm text-gray-600">{product.batch_id}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-blue-600">
                          {product.exportQuantity} boxes
                        </div>
                        <div className="text-sm text-gray-500">
                          of {product.quantity} available
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {errors.products && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.products}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200"
            >
              <MdSave className="mr-2" size={20} />
              Export {selectedProducts.length} Product{selectedProducts.length > 1 ? 's' : ''} ({calculateTotalQuantity()} boxes)
            </button>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => !loading && setShowPreview(false)}
            />

            {/* Modal */}
            <div className="relative w-full max-w-4xl rounded-lg bg-white p-6 shadow-xl dark:bg-navy-800">
              {/* Close button */}
              <button
                onClick={() => !loading && setShowPreview(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                disabled={loading}
              >
                <MdClose size={24} />
              </button>

              {/* Modal header */}
              <div className="mb-6 flex items-center space-x-4">
                <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                  <MdInventory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Preview Export Details
                </h3>
              </div>

              {/* Preview content */}
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <UsibrasLoader text="Exporting Products..." color="blue" size="large" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* General Information */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <MdPerson className="h-5 w-5" />
                        <span>Customer Name</span>
                      </div>
                      <p className="mt-1 font-medium text-gray-900 dark:text-white">
                        {formData.customerName}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <MdDateRange className="h-5 w-5" />
                        <span>Export Date</span>
                      </div>
                      <p className="mt-1 font-medium text-gray-900 dark:text-white">
                        {new Date(formData.exportDate).toLocaleDateString()}
                      </p>
                    </div>

                    {formData.purchaseOrder && (
                      <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                          <MdReceipt className="h-5 w-5" />
                          <span>Purchase Order</span>
                        </div>
                        <p className="mt-1 font-medium text-gray-900 dark:text-white">
                          {formData.purchaseOrder}
                        </p>
                      </div>
                    )}

                    {formData.lotEntry && (
                      <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                          <MdTag className="h-5 w-5" />
                          <span>Lot Entry</span>
                        </div>
                        <p className="mt-1 font-medium text-gray-900 dark:text-white">
                          {formData.lotEntry}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Selected Products */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Products to Export</h4>
                    <div className="space-y-3">
                      {selectedProducts.map((product, index) => (
                        <div key={product.id} className="rounded-lg bg-blue-50 p-4 dark:bg-navy-700">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <div className="text-sm text-gray-600 dark:text-gray-300">Product Type</div>
                              <div className="font-medium text-gray-900 dark:text-white">{product.productType}</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 dark:text-gray-300">Export Quantity</div>
                              <div className="font-medium text-gray-900 dark:text-white">{product.exportQuantity} boxes</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 dark:text-gray-300">Remaining</div>
                              <div className="font-medium text-gray-900 dark:text-white">{product.quantity - product.exportQuantity} boxes</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 dark:text-gray-300">Location</div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {product.lane || `Lane ${product.zone}`}, Slot {product.slot || product.rack}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Modal footer */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => !loading && setShowPreview(false)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? (
                    <UsibrasLoader size="small" color="white" />
                  ) : (
                    <>
                      <MdCheck className="mr-2" size={20} />
                      Confirm & Export ({selectedProducts.length})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Export; 